import React, { useEffect, useMemo, useState } from 'react';
import { X, Check, Search } from 'lucide-react';
import { useNavigationStore, NavItem } from '../../stores/navigationStore';
import { useAuth } from '../../contexts/AuthContext';
import { QUICK_ACTIONS_LIST, normalizeQuickActionIds } from '../../config/quickActions';

interface QuickActionsEditorProps {
    isOpen: boolean;
    onClose: () => void;
    allNavItems: NavItem[];
}

export function QuickActionsEditor({ isOpen, onClose, allNavItems }: QuickActionsEditorProps) {
    const { quickActions, setQuickActions } = useNavigationStore();
    const { user } = useAuth();
    const [selectedActions, setSelectedActions] = useState<string[]>(quickActions);
    const [searchQuery, setSearchQuery] = useState('');

    const availableActions = useMemo(() => {
        const navItemsById = new Map(allNavItems.map((item) => [item.id, item]));

        return QUICK_ACTIONS_LIST.flatMap((action) => {
            const navItem = navItemsById.get(action.id);
            return navItem ? [{ ...action, icon: navItem.icon }] : [];
        });
    }, [allNavItems]);

    useEffect(() => {
        if (!isOpen) return;
        const allowedIds = new Set(availableActions.map((action) => action.id));
        setSelectedActions(quickActions.filter((actionId) => allowedIds.has(actionId)));
        setSearchQuery('');
    }, [availableActions, isOpen, quickActions]);

    if (!isOpen) return null;

    const handleToggle = (itemId: string) => {
        if (selectedActions.includes(itemId)) {
            setSelectedActions(selectedActions.filter(id => id !== itemId));
        } else {
            setSelectedActions([...selectedActions, itemId]);
        }
    };

    const handleSave = () => {
        const normalized = normalizeQuickActionIds(selectedActions, 'quick actions editor');
        setQuickActions(normalized, user?.id);
        onClose();
    };

    const filteredItems = availableActions.filter(item =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Edit Quick Actions</h2>
                        <p className="text-sm text-gray-500">Select shortcuts for your dashboard ({selectedActions.length} selected)</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search available pages..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {filteredItems.map((item) => {
                            const Icon = item.icon;
                            const isSelected = selectedActions.includes(item.id);

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleToggle(item.id)}
                                    className={`flex items-center space-x-3 p-3 rounded-lg border text-left transition-all ${isSelected
                                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                                        {Icon ? <Icon className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{item.label}</p>
                                        <p className="text-xs text-gray-500 truncate">{item.path}</p>
                                    </div>
                                    {isSelected && (
                                        <div className="ml-auto">
                                            <Check className="w-4 h-4 text-blue-500" />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
}
